import * as acorn from 'acorn'
import * as astring from 'astring'
import { ConditionNode, ExprNode, GroupNode, GroupOperator, RawNode, ValueType } from './types'
import { DEFAULT_CONFIG, ExpressionEditorConfig, operatorsForField } from './fields'

// --- id generation (counter-based; deterministic, no Date/Math.random) ---
let idCounter = 0
const nextId = (): string => `node-${++idCounter}`

// Normalize loose equality to the strict operators the GUI exposes.
const normalizeOperator = (op: string): string => {
  if (op === '==') return '==='
  if (op === '!=') return '!=='
  return op
}

// --- factory helpers used by the GUI when adding nodes ---
export const createCondition = (config: ExpressionEditorConfig = DEFAULT_CONFIG): ConditionNode => {
  const field = config.fields[0]
  const operators = operatorsForField(config, field)
  return {
    id: nextId(),
    kind: 'condition',
    field: field ? field.value : '',
    operator: operators.length ? operators[0].value : '===',
    value: field && field.options && field.options.length ? field.options[0].value : '',
    valueType: 'string'
  }
}

export const createGroup = (
  config: ExpressionEditorConfig = DEFAULT_CONFIG,
  operator: GroupOperator = 'AND'
): GroupNode => ({
  id: nextId(),
  kind: 'group',
  operator,
  negate: false,
  children: [createCondition(config)]
})

export const createRaw = (source = 'Date.now() > 1780941852928'): RawNode => ({
  id: nextId(),
  kind: 'raw',
  source
})

// --- ESTree AST -> dotted path string (e.g. `$pet.attr.primary_color`) ---
const pathOf = (node: acorn.AnyNode): string => {
  if (!node) return ''
  switch (node.type) {
    case 'Identifier':
      return node.name
    case 'MemberExpression':
      return `${pathOf(node.object)}.${pathOf(node.property)}`
    case 'Literal':
      return String(node.value)
    default:
      return ''
  }
}

const literalValueType = (value: any): ValueType => {
  if (typeof value === 'number') return 'number'
  if (typeof value === 'boolean') return 'boolean'
  return 'string'
}

// A literal whose value maps cleanly onto the structured value control.
const isSimpleLiteral = (node: acorn.AnyNode): boolean =>
  node &&
  node.type === 'Literal' &&
  !node.regex &&
  ['string', 'number', 'boolean'].indexOf(typeof node.value) !== -1

// --- ESTree AST -> internal model ---
const toNode = (ast: acorn.AnyNode, known: Set<string>): ExprNode => {
  if (ast && ast.type === 'LogicalExpression') {
    const children: ExprNode[] = []
    collectLogical(ast, ast.operator, children, known)
    return {
      id: nextId(),
      kind: 'group',
      operator: ast.operator === '&&' ? 'AND' : 'OR',
      negate: false,
      children
    }
  }

  // `!( ... )` becomes a negated group.
  if (ast && ast.type === 'UnaryExpression' && ast.operator === '!') {
    const inner = toNode(ast.argument, known)
    if (inner.kind === 'group') {
      return { ...inner, negate: !inner.negate }
    }
    return { id: nextId(), kind: 'group', operator: 'AND', negate: true, children: [inner] }
  }

  // A simple `<known field> OP <literal>` becomes a structured condition.
  if (ast && ast.type === 'BinaryExpression') {
    const fieldPath = pathOf(ast.left)
    if (known.has(fieldPath) && isSimpleLiteral(ast.right)) {
      return {
        id: nextId(),
        kind: 'condition',
        field: fieldPath,
        operator: normalizeOperator(ast.operator),
        value: String((ast.right as any).value),
        valueType: literalValueType((ast.right as any).value)
      }
    }
  }

  // Anything else (calls, arrows, regex, unknown fields, complex comparisons): raw text leaf.
  return {
    id: nextId(),
    kind: 'raw',
    source: astring.generate(ast)
  }
}

// Flatten chains of the same logical operator into a single group's children.
const collectLogical = (ast: acorn.LogicalExpression, operator: string, out: ExprNode[], known: Set<string>): void => {
  ;[ast.left, ast.right].forEach(side => {
    if (side && side.type === 'LogicalExpression' && side.operator === operator) {
      collectLogical(side, operator, out, known)
    } else {
      out.push(toNode(side, known))
    }
  })
}

/**
 * Parse a JS boolean expression into the editable tree. Throws on invalid input.
 * The root is always a GroupNode so the editor can always add/edit conditions.
 */
export const parseExpression = (
  src: string,
  config: ExpressionEditorConfig = DEFAULT_CONFIG
): GroupNode => {
  const trimmed = (src || '').trim()
  if (!trimmed) {
    throw new Error('Expression is empty')
  }
  const program = acorn.parse(trimmed, { ecmaVersion: 2020 })
  const body = program.body
  if (body.length !== 1) {
    throw new Error('Enter a single expression (no `;`-separated statements)')
  }
  const statement = body[0]
  if (!statement || statement.type !== 'ExpressionStatement') {
    throw new Error('Input is not an expression')
  }
  const known = new Set(config.fields.map(f => f.value))
  const node = toNode(statement.expression, known)
  if (node.kind === 'group') {
    return node
  }
  return { id: nextId(), kind: 'group', operator: 'AND', negate: false, children: [node] }
}

/**
 * Validate that a string is a single, syntactically valid JS expression.
 * Returns an error message, or null when valid. Use to check raw-node text
 * and the re-serialized output before treating it as a usable expression.
 */
export const validateExpression = (src: string): string | null => {
  const trimmed = (src || '').trim()
  if (!trimmed) {
    return 'Expression is empty'
  }
  try {
    const body = (acorn.parse(trimmed, { ecmaVersion: 2020 })).body
    if (body.length !== 1 || body[0].type !== 'ExpressionStatement') {
      return 'Must be a single expression'
    }
    return null
  } catch (e) {
    return (e as Error).message
  }
}

/**
 * antd `Form.Item` validator for an expression field. Lets the `required` rule
 * handle emptiness; rejects with the parser message when the value is invalid JS.
 * Usage: `rules={[{ validator: expressionFieldValidator }]}`.
 */
export const expressionFieldValidator = async (_rule: any, value: string): Promise<void> => {
  if (!value || !value.trim()) return
  const error = validateExpression(value)
  if (error) {
    throw new Error(error)
  }
}

// --- internal model -> JS string ---
const formatValue = (node: ConditionNode): string => {
  if (node.valueType === 'string') {
    return JSON.stringify(node.value)
  }
  // number / boolean / raw are emitted verbatim
  return node.value
}

export const serializeExpression = (node: ExprNode): string => {
  if (node.kind === 'group') {
    const joiner = node.operator === 'AND' ? ' && ' : ' || '
    const inner = node.children
      .map(child => {
        const serialized = serializeExpression(child)
        // A negated child group already emits `!( ... )`; other groups need parens.
        if (child.kind === 'group') {
          return child.negate ? serialized : `(${serialized})`
        }
        return serialized
      })
      .filter(Boolean)
      .join(joiner)
    return node.negate ? `!(${inner})` : inner
  }
  if (node.kind === 'raw') {
    return node.source.trim()
  }
  return `${node.field} ${node.operator} ${formatValue(node)}`.trim()
}
